import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import Collapse from '@rio-cloud/rio-uikit/Collapse';

import CommonRightToolBar from '@/features/common/components/CommonRightToolBar';
import { TABLE_MAINTENANCE_DATA } from '@/features/common/components/dialogs/dialogConstants';
import { MaintenanceTable } from '@/features/common/components/maintenance-table/MaintenanceTable';
import { useUserSettings } from '@/features/common/hooks/useUserSettings';
import { AppointmentCell } from '@/features/maintenance-data/cells/AppointmentCell';
import { ComponentCell } from '@/features/maintenance-data/cells/ComponentCell';
import { DueAtCell } from '@/features/maintenance-data/cells/DueAtCell';
import { DueInCell } from '@/features/maintenance-data/cells/DueInCell';
import { StatusCell } from '@/features/maintenance-data/cells/StatusCell';
import { MAINTENANCE_DATA_COLUMNS_CONFIG, statusMapper } from '@/features/maintenance-data/columns';
import { FILTER_MSG } from '@/features/maintenance-data/filters/constants';
import MaintenanceDataFilters from '@/features/maintenance-data/filters/MaintenanceDataFilters';
import SelectedFilterTags from '@/features/maintenance-data/filters/SelectedFilterTags';
import { useMaintenanceDataRequestData } from '@/features/maintenance-data/hooks/useMaintenanceDataRequestData';
import { useMaintenanceTableContext } from '@/features/maintenance-data/hooks/useMaintenanceTableContext';
import { useMarkSolvedActions } from '@/features/maintenance-data/hooks/useMarkSolvedActions';
import { useRowSelection } from '@/features/maintenance-data/hooks/useRowSelection';
import { MaintenanceDataLeftToolbar } from '@/features/maintenance-data/MaintenanceDataLeftToolbar';
import { MaintenanceDataColumns, MaintenanceDataItem, MaintenanceDataSettings } from '@/generated/GeneratedApiTypes';
import { useDownloadMaintenanceDataCsvFileMutation } from '@/services/vehicleDiagnosticsApi';

import '@/features/common/components/maintenance-table/style.css';

// TableRow renders the vehicle/vin/customer/workshop cells itself, then calls rowDetailRenderer
// for the remaining cells. Maintenance-data rows are flat, so we emit one <td> per extra column.
const rowDetailRenderer = (data: MaintenanceDataItem) => (
    <>
        <StatusCell row={data} />
        <ComponentCell row={data} />
        <DueAtCell row={data} />
        <DueInCell row={data} />
        <AppointmentCell row={data} />
    </>
);

export const MaintenanceDataTable = () => {
    const intl = useIntl();
    const { settings: userSettings } = useUserSettings<MaintenanceDataSettings>(TABLE_MAINTENANCE_DATA);
    const params = useMaintenanceDataRequestData({});
    const [downloadFile, { isLoading: isDownloading }] = useDownloadMaintenanceDataCsvFileMutation();
    const { pagination, sorting, data, isLoading, isFetching, isError, error } = params;

    const rows = useMemo(() => data?.content ?? [], [data?.content]);
    // Selection resets whenever the request changes — page, filters, sort, page size or the
    // asset-tree selection — since any of those change which rows are shown.
    const selection = useRowSelection(rows, params.queryParams);
    const { availableActions, sendSchedule, sendEdit, sendCancel } = useMaintenanceTableContext(selection);
    const { requestMarkSolved, requestRevertSolved, isUpdating: isUpdatingSolved } = useMarkSolvedActions(selection);

    const visibleColumns = useMemo(() => {
        const columns = userSettings?.columns;
        return MAINTENANCE_DATA_COLUMNS_CONFIG.filter((col) => {
            if (columns && col.name in columns) {
                return columns[col.name as keyof MaintenanceDataColumns] ?? false;
            }
            return !col.hidden;
        }).map((col) => ({ ...col, hidden: false }));
    }, [userSettings]);

    const handleDownload = useCallback(() => {
        downloadFile(params.queryParams);
    }, [downloadFile, params.queryParams]);

    return (
        <div className="maintenance-data-view padding-20">
            <div className="display-flex justify-content-between align-items-center margin-bottom-10">
                <button
                    type="button"
                    className="btn btn-default"
                    onClick={() => params.setShowFilters(!params.showFilters)}
                >
                    <FormattedMessage id={`${FILTER_MSG}.title`} />
                    <span className={`rioglyph rioglyph-chevron-${params.showFilters ? 'up' : 'down'} margin-left-5`} />
                </button>
                <Checkbox
                    checked={params.activeMaintenance}
                    onChange={(event) => params.setActiveMaintenance(event.target.checked)}
                >
                    <FormattedMessage id={`${FILTER_MSG}.showOnlyActiveMaintenance`} />
                </Checkbox>
            </div>

            <Collapse open={params.showFilters} unmountOnExit>
                <MaintenanceDataFilters
                    status={params.status}
                    onStatusChange={params.setStatus}
                    solvedByCustomer={params.solvedByCustomer}
                    onSolvedByCustomerChange={params.setSolvedByCustomer}
                    componentType={params.componentType}
                    onComponentTypeChange={params.setComponentType}
                    component={params.component}
                    onComponentChange={params.setComponent}
                    dueDateFrom={params.dueDateFrom}
                    dueDateTo={params.dueDateTo}
                    dueAtRangeType={params.dueAtRangeType}
                    onDueAtChange={({ dueDateFrom, dueDateTo, dueAtRangeType }) => {
                        params.setDueDateFrom(dueDateFrom);
                        params.setDueDateTo(dueDateTo);
                        params.setDueAtRangeType(dueAtRangeType);
                    }}
                    dueMileageMin={params.dueMileageMin}
                    dueMileageMax={params.dueMileageMax}
                    onDueInChange={({ from, to }) => {
                        params.setDueMileageMin(from);
                        params.setDueMileageMax(to);
                    }}
                    mileageMin={params.mileageMin}
                    mileageMax={params.mileageMax}
                    onMileageChange={({ from, to }) => {
                        params.setMileageMin(from);
                        params.setMileageMax(to);
                    }}
                    dueRangesOrGate={params.dueRangesOperator === 'OR'}
                    onDueRangesOrGateChange={(value) => params.setDueRangesOperator(value ? 'OR' : 'AND')}
                    isLoading={isLoading}
                />
            </Collapse>

            <SelectedFilterTags
                status={params.status}
                onRemoveStatus={(s) => params.setStatus(params.status.filter((v) => v !== s))}
                solvedByCustomer={params.solvedByCustomer}
                onRemoveSolvedByCustomer={() => params.setSolvedByCustomer(false)}
                componentType={params.componentType}
                onRemoveComponentType={(t) => params.setComponentType(params.componentType.filter((v) => v !== t))}
                component={params.component}
                onRemoveComponent={(slug) => params.setComponent(params.component.filter((v) => v !== slug))}
                dueDateFrom={params.dueDateFrom}
                dueDateTo={params.dueDateTo}
                onRemoveDueAt={() => {
                    params.setDueDateFrom(undefined);
                    params.setDueDateTo(undefined);
                    params.setDueAtRangeType(undefined);
                }}
                dueMileageMin={params.dueMileageMin}
                dueMileageMax={params.dueMileageMax}
                onRemoveDueIn={() => {
                    params.setDueMileageMin(undefined);
                    params.setDueMileageMax(undefined);
                }}
                mileageMin={params.mileageMin}
                mileageMax={params.mileageMax}
                onRemoveMileage={() => {
                    params.setMileageMin(undefined);
                    params.setMileageMax(undefined);
                }}
                dueRangesOrGate={params.dueRangesOperator === 'OR'}
                onRemoveDueRangesOrGate={() => params.setDueRangesOperator('AND')}
                onResetAll={params.resetFilters}
            />

            <MaintenanceTable
                className="maintenance-data-table"
                columnConfig={visibleColumns}
                enableRowSelection
                rowSelection={{
                    selectedIds: selection.selectedIds,
                    onToggleRow: selection.toggle,
                    onToggleAll: selection.toggleAll,
                    allSelected: selection.allSelected,
                    someSelected: selection.someSelected,
                    rowSelectionKey: (row: MaintenanceDataItem) => row.resource_id,
                }}
                queryState={{ isError, error: error as FetchBaseQueryError, isFetching, isLoading }}
                pageMetadata={data?.page}
                pagination={pagination}
                sorting={sorting}
                rows={rows}
                leftToolbar={
                    <MaintenanceDataLeftToolbar
                        selection={selection}
                        availableActions={availableActions}
                        isUpdating={isUpdatingSolved}
                        onMarkSolved={requestMarkSolved}
                        onRevertSolved={requestRevertSolved}
                        onSchedule={sendSchedule}
                        onEditAppointment={sendEdit}
                        onCancelAppointment={sendCancel}
                    />
                }
                rightToolbar={
                    <CommonRightToolBar
                        onExportClick={handleDownload}
                        isDownloading={isDownloading}
                        settingsDialog={TABLE_MAINTENANCE_DATA}
                    />
                }
                rowStatusMapper={statusMapper}
                rowDetailRenderer={rowDetailRenderer}
                detailViewTabParam="VIEW_DETAIL_MAINTENANCE_AND_FILL_LEVELS"
            />
        </div>
    );
};
